import React from 'react';
import { User, Users } from 'lucide-react';
import type { ManualInviteAccountFor } from '@court-sync/shared';
import { cn } from '@/lib/utils';

export type ManualInviteAccountTypeSelectorProps = {
  value: ManualInviteAccountFor | null;
  onChange: (value: ManualInviteAccountFor) => void;
  studentName: string;
  disabled?: boolean;
};

export function ManualInviteAccountTypeSelector({
  value,
  onChange,
  studentName,
  disabled = false,
}: ManualInviteAccountTypeSelectorProps) {
  return (
    <div className="mb-6">
      <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
        Who is this account for?
      </h3>
      <div className="flex flex-col gap-3 sm:flex-row">
        <label
          className={cn(
            'flex flex-1 cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
            value === 'myself'
              ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500 ring-offset-2 ring-offset-background dark:border-primary-400 dark:bg-primary-950/30 dark:ring-offset-gray-800'
              : 'border-gray-200 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700/50',
            disabled && 'pointer-events-none opacity-60'
          )}
        >
          <input
            type="radio"
            name="manualInviteAccountFor"
            value="myself"
            checked={value === 'myself'}
            onChange={() => onChange('myself')}
            disabled={disabled}
            className="mt-1 h-4 w-4 shrink-0 text-primary-600 focus:ring-primary-500 border-gray-300"
          />
          <span className="min-w-0">
            <span className="flex items-center gap-1.5 font-medium text-gray-900 dark:text-white">
              <User className="h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden />
              Myself
            </span>
            <span className="mt-1 block text-sm text-gray-500 dark:text-gray-400">
              I am {studentName}. Link this account to my player profile.
            </span>
          </span>
        </label>
        <label
          className={cn(
            'flex flex-1 cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
            value === 'child'
              ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500 ring-offset-2 ring-offset-background dark:border-primary-400 dark:bg-primary-950/30 dark:ring-offset-gray-800'
              : 'border-gray-200 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700/50',
            disabled && 'pointer-events-none opacity-60'
          )}
        >
          <input
            type="radio"
            name="manualInviteAccountFor"
            value="child"
            checked={value === 'child'}
            onChange={() => onChange('child')}
            disabled={disabled}
            className="mt-1 h-4 w-4 shrink-0 text-primary-600 focus:ring-primary-500 border-gray-300"
          />
          <span className="min-w-0">
            <span className="flex items-center gap-1.5 font-medium text-gray-900 dark:text-white">
              <Users className="h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden />
              My child
            </span>
            <span className="mt-1 block text-sm text-gray-500 dark:text-gray-400">
              I am a parent or guardian managing {studentName}&apos;s bookings.
            </span>
          </span>
        </label>
      </div>
    </div>
  );
}
